import {
  Box,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
  Link,
  IconButton,
  Menu,
  Divider,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import {
  convertESTtoUserLocalDateAndTime,
  debounceEventHandler,
  getFormattedStatsCount,
  handleErrorMessage,
  initialPageFilterState,
  openSuccessNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { useState, useRef, useEffect, ChangeEvent } from "react";
import { useHistory, useLocation } from "react-router-dom";
import contactsStyles from "screens/Contacts/Contacts.styles";
import { useTitle } from "utils/UseTitle";
import SearchIcon from "assets/icons1/search_icon.svg";
import CustomTable from "global/components/CustomTable/CustomTable";
import { groupsOptions, groupsTableHeader } from "./GropsConstants";
import {
  deleteContactGroup,
  fetchGroupCount,
  fetchGroups,
  getSubordinatesList,
} from "./GroupsService";
import { RouterLink } from "components/router-link";
import MoreActionIcon from "assets/icons1/MoreActionIcon.svg";
import AddContactsModal from "screens/Contacts/AddContacts/AddContactsModal";
import ConfirmationModal from "screens/Templates/Components/ConfirmationModal";
import { selectIsManager } from "redux/generalSlice";
import { useAppSelector } from "utils/hooks";
import { pageFilters } from "models/interfaces";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";

const Groups = () => {
  useTitle(title.GROUPS);
  const theme = useTheme();
  const history = useHistory();
  const classes = contactsStyles(theme);
  const isManager = useAppSelector(selectIsManager);
  const [groups, setGroups] = useState<string[]>([]);
  const [count, setCount] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
  const [addContactsModal, setAddContactsModal] = useState<boolean>(false);
  const [isDeleteGroupModal, setIsDeleteGroupModal] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<any>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const groupTypeFromUrl = urlParams.get("category");
  const searchFromUrl = urlParams.get("search");
  const hasAccessToAddContact = hasAccessTo(
    strings.contact,
    strings.addPermission
  );
  const hasAccessToDeleteContact = hasAccessTo(
    strings.contact,
    strings.deletePermission
  );

  const hasContactFetchPermission = hasAccessTo(
    strings.contact,
    strings.fetchPermission
  );

  const [groupsFilters, setGroupsFilters] = useState<pageFilters>(
    initialPageFilterState(
      pageFromUrl,
      rowsPerPageFromUrl,
      searchFromUrl ?? "",
      groupTypeFromUrl ?? "all"
    )
  );

  useEffect(() => {
    const { page, rowsPerPage, searchText, dropdownValue } = groupsFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;

    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}&category=${dropdownValue}`
    );
    getTableData();
  }, [groupsFilters]);

  const handleRowsPageData = (rowPerPage: number) => {
    setGroupsFilters({
      ...groupsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const handleChangePage = async (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    setGroupsFilters({ ...groupsFilters, page });
  };

  const handleDropDownChange = (event: any) => {
    const dropdownValue = event.target.value;
    setGroupsFilters({
      ...groupsFilters,
      dropdownValue,
      page: 1,
    });
  };

  const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setGroupsFilters({
      ...groupsFilters,
      searchText,
      page: 1,
    });
  };

  const getTableData = async () => {
    const { page, rowsPerPage, searchText, dropdownValue } = groupsFilters;
    try {
      setIsLoading(true);
      setAnchorEl(null);
      const [subordinates, count] = await Promise.all([
        getSubordinatesList(),
        fetchGroupCount(dropdownValue!, searchText),
      ]);
      setCount(count);
      const subordinatesUserIds = subordinates?.map(
        (subordinate: any) => subordinate.userId
      );
      await getTableDataAccordingToPage(
        page,
        count,
        dropdownValue!,
        rowsPerPage,
        searchText.trim(),
        subordinatesUserIds
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    page: number,
    count: number,
    dropdownValue: string,
    rowsPerPage: number,
    searchText: string,
    subordinates: number[]
  ) => {
    try {
      let groupsRes: any[] = [];
      groupsRes = await fetchGroups(
        page,
        rowsPerPage,
        dropdownValue,
        searchText
      );
      const tableData = convertDataToTableFormat(subordinates, groupsRes);
      setGroups([...tableData]);
    } catch (error) {
      throw error;
    }
  };

  const convertDataToTableFormat = (subordinates: number[], data: any) => {
    return data.map((group: any) => {
      return {
        rowId: group.id,
        name: (
          <Stack direction="column">
            <Link
              color="text.primary"
              variant="subtitle2"
              href={`${viewpaths.viewGroupsViewPath}/${group.id}`}
              component={RouterLink}
              onClick={(e) => {
                e.preventDefault();
                history.push(`${viewpaths.viewGroupsViewPath}/${group.id}`, {
                  group,
                  subordinates,
                });
              }}
            >
              {group.name}
            </Link>
          </Stack>
        ),
        contacts: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {getFormattedStatsCount(group.recipientCount)}
          </Typography>
        ),
        createdOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              group.createdOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        updatedOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              group.updatedOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        type: (
          <Typography
            sx={{
              color: group.public ? "#47AA12" : "#475569",
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                ...classes.contactStatusBox,
                backgroundColor: group.public ? "#47AA12" : "#475569",
              }}
            />
            {group.public ? "Public" : "Private"}
          </Typography>
        ),
        action: subordinates.includes(group.userId) && (
          <IconButton
            size="small"
            sx={{
              color: "#fff",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(e: any) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
              setSelectedGroup(group);
            }}
          >
            <img src={MoreActionIcon} width="4px" alt="MoreActionIcon" />
          </IconButton>
        ),
      };
    });
  };

  const getAddContactsModal = () => {
    return (
      <AddContactsModal
        handleDialogClose={() => setAddContactsModal(false)}
        group={selectedGroup}
        isVisible={addContactsModal}
        contactsFilters={groupsFilters}
        setContactsFilters={setGroupsFilters}
      />
    );
  };
  const deleteGroup = async (groupId: any) => {
    try {
      setAnchorEl(null);
      setIsDeleteGroupModal(false);
      setIsApiLoading(true);
      const response = await deleteContactGroup(groupId);
      openSuccessNotification(response.message);
      setIsApiLoading(false);
      setGroupsFilters({
        ...groupsFilters,
        rowsPerPage: 10,
        page: 1,
      });
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsApiLoading(false);
    }
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="contacts_search_box"
            placeholder="Search Groups"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            autoComplete="off"
            fullWidth
            onChange={debounceEventHandler(
              handleSearchOnChange,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
          {isManager && (
            <>
              <Divider />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Select
                  variant="outlined"
                  id="contacts_category_dropdown"
                  value={groupsFilters.dropdownValue}
                  onChange={handleDropDownChange}
                  sx={classes.dropDown}
                  disabled={isLoading}
                >
                  {groupsOptions.map((data, index: number) => (
                    <MenuItem
                      key={index}
                      value={data.value}
                      sx={classes.dropDownMenuItem}
                    >
                      {data.label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </>
          )}
        </Stack>
      </CenteredCardLayout>
    );
  };
  const getGroupsPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h5" color="text.primary">
            Groups
          </Typography>
        </Stack>
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={groupsTableHeader}
              rows={groups}
              page={groupsFilters.page}
              rowsPerPage={groupsFilters.rowsPerPage}
              onRowsPerPageChange={handleRowsPageData}
              onPageChange={handleChangePage}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={handleChangePage}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
        {addContactsModal && getAddContactsModal()}
      </Stack>
    );
  };
  return (
    <>
      {hasContactFetchPermission ? (
        <>
          <CenteredAuthenticatedLayout>
            {getGroupsPage()}
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {hasAccessToAddContact && (
                <MenuItem
                  value={"importContacts"}
                  sx={{
                    fontSize: "14px",
                    "&:not(:last-child)": {
                      borderBottom: "1px solid #e0e0e0",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#FFFFFF",
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                    },
                    "&:focus": { backgroundColor: "#FFFFFF" },
                  }}
                  onClick={() => {
                    setAddContactsModal(true);
                    setAnchorEl(null);
                  }}
                >
                  Add Contact
                </MenuItem>
              )}

              {hasAccessToDeleteContact && (
                <MenuItem
                  value={"deleteGroup"}
                  sx={{
                    fontSize: "14px",
                    "&:not(:last-child)": {
                      borderBottom: "1px solid #e0e0e0",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#FFFFFF",
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                    },
                    "&:focus": { backgroundColor: "#FFFFFF" },
                  }}
                  onClick={() => setIsDeleteGroupModal(true)}
                >
                  Delete Group
                </MenuItem>
              )}
            </Menu>
            {isDeleteGroupModal && (
              <ConfirmationModal
                isVisible={isDeleteGroupModal}
                handleDialogClose={() => {
                  setAnchorEl(null);
                  setIsDeleteGroupModal(false);
                }}
                title="Please Confirm"
                subtitle2={
                  <Typography>
                    {`${"Are you sure you want to delete "}`}
                    <span style={{ fontWeight: 700 }}>
                      {selectedGroup.name}?
                    </span>
                  </Typography>
                }
                btn1Title="Delete"
                btn1Variant="outlined"
                btn2Title="Cancel"
                btn2Variant="contained"
                btn1OnClick={() => deleteGroup(selectedGroup.id)}
                btn2OnClick={() => {
                  setAnchorEl(null);
                  setIsDeleteGroupModal(false);
                }}
              />
            )}
          </CenteredAuthenticatedLayout>
          <CustomLoader isLoading={isApiLoading} />
        </>
      ) : (
        <Unauthorized permissionDenied={hasContactFetchPermission} />
      )}
    </>
  );
};

export default Groups;
